import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  UserCheck, Check, X, ShieldCheck, FileSignature, Globe2, AlertTriangle,
  ExternalLink, Lock, Briefcase, Scale, Building2, ArrowRight, FileText,
  Hash,
} from 'lucide-react';
import { useContent } from '../../lib/content';
import { api, pickLocale } from '../../lib/api';

/**
 * LikenessVault — V2 (refuerzo). The "right of likeness" moat.
 *
 *   1. Hero block (eyebrow + title + lead) with stats strip.
 *   2. Three-actor flow diagram (TALENT → AGENCY → LEGAL → MODARIA VAULT)
 *      with animated SVG path so it floats as you scroll.
 *   3. Three-state grid (synthetic / real-with-release / real-no-release).
 *   4. Talent Release Certificate live card + clause ticker (legal text
 *      auto-scrolling like a Bloomberg crawl on the side).
 *   5. Compliance check grid + verifiable footer.
 *   6. Bottom CTA strip with two pathways.
 */

// Auto-scrolling clause ticker
const CLAUSES_ES = [
  '§1.1 El permiso cubre exclusivamente los usos descritos en el alcance.',
  '§1.2 Queda PROHIBIDO entrenar modelos de IA con la imagen del modelo.',
  '§2.1 El modelo puede CANCELAR el permiso con 30 días de aviso.',
  '§2.2 La cancelación obliga a MODARIA a retirar todas las imágenes vinculadas.',
  '§3.1 El territorio se aplica a la publicación y a la distribución.',
  '§4.1 La compensación se calcula por campaña, no por imagen derivada.',
  '§5.1 Los menores quedan excluidos por defecto. La inclusión exige tutor + autoridad legal.',
  '§6.1 La firma digital única del contrato es la prueba para cualquier auditoría.',
];
const CLAUSES_EN = [
  '§1.1 The permission covers only the uses described in the scope.',
  '§1.2 Training AI models on the model\'s likeness is FORBIDDEN.',
  '§2.1 The model may REVOKE the permission with 30-day notice.',
  '§2.2 Revocation forces MODARIA to remove every linked image.',
  '§3.1 Territory applies to publication and distribution.',
  '§4.1 Compensation is calculated per campaign, not per derived image.',
  '§5.1 Minors are excluded by default. Inclusion requires guardian + legal authority.',
  '§6.1 The unique digital contract signature is the proof for any audit.',
];

// Plataforma capabilities (sin contadores falsos — eso sería maquillaje verde).
const STATS = [
  { value: 'IMG · VIDEO',  es: 'Cubre imagen y vídeo',           en: 'Covers image and video' },
  { value: 'AI · IA',      es: 'Detección automática de caras',  en: 'Automatic face detection' },
  { value: 'BLOQUEA',      es: 'Bloquea si no hay derechos',     en: 'Blocks publication if no rights' },
  { value: '∞',            es: 'Permisos ilimitados en tu casa', en: 'Unlimited in-house permissions' },
];

function ActorFlow({ locale }) {
  // 4-step flow with SVG connectors animated on view
  const steps = [
    { Icon: UserCheck, label: { es: 'MODELO',         en: 'TALENT' },         sub: { es: 'modelo + agencia',  en: 'model + agency' } },
    { Icon: Briefcase, label: { es: 'AGENCIA',        en: 'AGENCY' },         sub: { es: 'términos · alcance', en: 'terms · scope' } },
    { Icon: Scale,     label: { es: 'LEGAL',          en: 'LEGAL' },          sub: { es: 'cláusulas para IA',  en: 'AI clauses' } },
    { Icon: Building2, label: { es: 'CAJA FUERTE',    en: 'VAULT' },          sub: { es: 'firmado · auditable', en: 'signed · audited' } },
  ];
  return (
    <div className="relative grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-0 mb-12 md:mb-16" data-testid="likeness-actor-flow">
      {steps.map((s, i) => {
        const Ic = s.Icon;
        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: i * 0.12 }}
            className="relative border border-line p-5 md:p-6 bg-bg/40 hover:border-acid/50 transition-colors group"
          >
            <div className="flex items-center justify-between mb-4">
              <Ic size={16} className="text-acid group-hover:scale-110 transition-transform" />
              <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-ink-dim">0{i + 1}</span>
            </div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink mb-1">{pickLocale(s.label, locale)}</div>
            <div className="text-eyebrow text-ink-muted">{pickLocale(s.sub, locale)}</div>
            {/* Connector arrow (desktop) */}
            {i < steps.length - 1 && (
              <div className="hidden md:flex absolute top-1/2 -right-3 -translate-y-1/2 z-10 w-6 h-6 items-center justify-center">
                <motion.div
                  initial={{ opacity: 0, x: -6 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.12 + 0.3 }}
                  className="bg-bg border border-acid/50 rounded-full w-6 h-6 flex items-center justify-center"
                >
                  <ArrowRight size={11} className="text-acid" />
                </motion.div>
              </div>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}

function ClauseTicker({ locale }) {
  const clauses = locale === 'es' ? CLAUSES_ES : CLAUSES_EN;
  // Triplicate to make the marquee seamless.
  const all = [...clauses, ...clauses, ...clauses];
  return (
    <div className="border border-line bg-bg/40 overflow-hidden h-[180px] relative" data-testid="likeness-clause-ticker">
      <div className="absolute inset-x-0 top-0 px-3 py-1.5 bg-bg/70 backdrop-blur-sm border-b border-line z-10 flex items-center gap-2">
        <FileText size={11} className="text-acid" />
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink">
          {locale === 'es' ? 'CLÁUSULAS · TICKER LEGAL' : 'CLAUSES · LEGAL TICKER'}
        </span>
        <span className="ml-auto inline-block w-1 h-1 rounded-full bg-acid animate-pulse" />
      </div>
      <div className="absolute inset-0 pt-9">
        <div className="animate-marquee-vertical flex flex-col gap-3 px-4 py-3">
          {all.map((c, i) => (
            <div key={i} className="font-mono text-[11px] leading-relaxed text-ink-muted">
              <span className="text-acid mr-2">●</span>{c}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function LikenessVault() {
  const { content, locale } = useContent();
  const data = content.likeness_vault || {};
  const [release, setRelease] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/compliance/sample-likeness')
      .then(({ data: d }) => { if (alive) setRelease(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const t = (k, fb = '') => pickLocale(data[k], locale, fb);

  const states = [
    {
      key: 'synthetic',
      label: locale === 'es' ? 'Persona 100% virtual' : 'Fully virtual persona',
      sub: locale === 'es' ? 'No hay derecho de imagen que cubrir.' : 'No likeness rights apply.',
      icon: ShieldCheck,
      tone: 'ink',
      detail: locale === 'es'
        ? 'MODARIA registra el motor de IA + parámetros + aprobación humana. Listo.'
        : 'MODARIA logs the AI engine + parameters + human approval. Done.',
    },
    {
      key: 'real_release',
      label: locale === 'es' ? 'Modelo real con autorización firmada' : 'Real model · signed permission',
      sub: locale === 'es' ? 'Nuestro caso ideal.' : 'Our ideal case.',
      icon: FileSignature,
      tone: 'acid',
      detail: locale === 'es'
        ? 'Autorización del Modelo con cláusulas para IA + alcance + territorio + derecho a cancelar. Adjunta al Informe de Cumplimiento.'
        : 'Model Permission with AI clauses + scope + territory + right to revoke. Attached to the Compliance Report.',
    },
    {
      key: 'real_no_release',
      label: locale === 'es' ? 'Modelo real sin autorización' : 'Real model · no permission',
      sub: locale === 'es' ? 'Riesgo que bloquea.' : 'Blocking risk.',
      icon: AlertTriangle,
      tone: 'danger',
      detail: locale === 'es'
        ? 'MODARIA detecta la cara real y bloquea la publicación hasta cubrir los derechos.'
        : 'MODARIA detects the real face and blocks publication until rights are cleared.',
    },
  ];

  return (
    <section
      id="likeness"
      data-testid="likeness-vault"
      className="relative section bg-bg-secondary border-y border-line overflow-hidden"
    >
      <div className="container-x">
        {/* HEADER */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-10 md:mb-14">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-2 mb-5">
              <UserCheck size={14} className="text-acid" />
              <span className="text-eyebrow text-acid">{t('eyebrow', 'EL DERECHO DE CARA')}</span>
            </div>
            <h2
              data-testid="likeness-title"
              className="font-display text-4xl md:text-5xl lg:text-6xl xl:text-7xl tracking-tight leading-[0.98] text-ink"
            >
              {t('title', 'Cuando la cara es de alguien real, MODARIA tiene su contrato.')}
            </h2>
          </div>
          <div className="lg:col-span-6 lg:pt-6">
            <p className="text-ink-muted text-base md:text-lg leading-relaxed mb-6">
              {t('lead', 'Las plataformas de IA no preguntan a quién pertenece esa cara, ese cuerpo o esa voz. MODARIA sí: si la imagen se construye sobre la cara de una persona real, exige la autorización firmada, la une con cláusulas específicas para uso con IA y la adjunta al Informe de Cumplimiento.')}
            </p>
            <p className="text-ink-muted text-base leading-relaxed">
              {t('lead2', 'Personas virtuales cuando funcionan. Personas reales cuando las necesitas. Y, en ambos casos, papeles en regla.')}
            </p>
          </div>
        </div>

        {/* CAPABILITIES STRIP (not fake aggregates) */}
        <div className="grid grid-cols-2 md:grid-cols-4 border-y border-line mb-12 md:mb-16 divide-x divide-line" data-testid="likeness-stats">
          {STATS.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="px-4 md:px-6 py-7 md:py-8"
              data-testid={`likeness-stat-${i}`}
            >
              <div className="font-display text-2xl md:text-3xl lg:text-4xl text-ink leading-none">
                {s.value}
              </div>
              <div className="text-eyebrow text-ink-muted mt-3 leading-tight">
                {locale === 'es' ? s.es : s.en}
              </div>
            </motion.div>
          ))}
        </div>

        {/* ACTOR FLOW */}
        <div className="text-eyebrow text-ink-muted mb-5">
          {locale === 'es' ? 'CADENA DE COBERTURA · 4 ACTORES' : 'COVERAGE CHAIN · 4 ACTORS'}
        </div>
        <ActorFlow locale={locale} />

        {/* 3 STATES */}
        <div className="text-eyebrow text-ink-muted mb-5">
          {locale === 'es' ? 'ESCENARIOS QUE GESTIONAMOS' : 'SCENARIOS WE COVER'}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-16 md:mb-20" data-testid="likeness-states">
          {states.map((s, i) => {
            const Ic = s.icon;
            const toneClass =
              s.tone === 'acid' ? 'border-acid/60 bg-acid/5'
              : s.tone === 'danger' ? 'border-danger/40 bg-danger/5'
              : 'border-line';
            const iconClass =
              s.tone === 'acid' ? 'text-acid'
              : s.tone === 'danger' ? 'text-danger'
              : 'text-ink-muted';
            return (
              <motion.div
                key={s.key}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className={`border ${toneClass} p-6 md:p-7`}
                data-testid={`likeness-state-${s.key}`}
              >
                <div className="flex items-center justify-between mb-5">
                  <Ic size={20} className={iconClass} />
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-dim">
                    0{i + 1}
                  </span>
                </div>
                <div className="font-display text-xl md:text-2xl text-ink leading-tight mb-1.5">{s.label}</div>
                <div className="text-eyebrow text-ink-muted mb-4">{s.sub}</div>
                <p className="text-ink-muted text-sm leading-relaxed">{s.detail}</p>
              </motion.div>
            );
          })}
        </div>

        {/* TALENT RELEASE CERTIFICATE + CLAUSE TICKER */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-12">
          <div className="lg:col-span-4">
            <div className="text-eyebrow text-ink-muted mb-4">{t('cert_label', 'TALENT RELEASE CERTIFICATE')}</div>
            <h3 className="font-display text-3xl md:text-4xl leading-tight text-ink mb-5">
              {t('cert_title', 'El documento que el regulador, la agencia y el modelo firman.')}
            </h3>
            <p className="text-ink-muted text-sm leading-relaxed mb-6">
              {t('cert_body', 'Nombre + agencia + scope + territorio + cláusulas IA + derecho de revocación + lista de activos vinculados. Validación notarial opcional. Hash criptográfico del contrato.')}
            </p>
            <ul className="space-y-2.5 mb-6">
              {(data.cert_bullets || [
                { es: 'Cláusula explícita de uso con IA generativa', en: 'Explicit clause for generative AI use' },
                { es: 'Cláusula de NO entrenamiento sobre el likeness', en: 'No-training clause on likeness' },
                { es: 'Derecho de revocación con plazo definido', en: 'Defined-term revocation right' },
                { es: 'Trazabilidad de cuántos activos lo usan', en: 'Traceability of every asset using it' },
                { es: 'Exportable como PDF firmado para auditor', en: 'Exportable as signed PDF for the auditor' },
              ]).map((b, i) => (
                <li key={i} className="flex items-start gap-2.5 text-ink text-sm">
                  <Check size={13} className="text-acid mt-1 shrink-0" />
                  <span>{pickLocale(b, locale)}</span>
                </li>
              ))}
            </ul>
            {/* Hash highlight */}
            <div className="border border-line bg-bg/40 p-3 flex items-center gap-2.5">
              <Hash size={11} className="text-acid shrink-0" />
              <div className="min-w-0">
                <div className="text-eyebrow text-ink-dim mb-0.5">{locale === 'es' ? 'FIRMA DEL CONTRATO' : 'CONTRACT SIGNATURE'}</div>
                <div className="font-mono text-[10px] text-ink-muted truncate">
                  {release?.signature?.contract_hash?.slice(0, 56) || 'sha256:7c2b1a09f8e7d6c5b4a392817160f5e4d3c2b1a09f8e7d6c5b4'}…
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7 }}
              className="border border-line-strong bg-bg/60 backdrop-blur-sm"
              data-testid="likeness-cert-card"
            >
              <div className="flex items-center justify-between border-b border-line-strong px-5 py-3 bg-bg/40">
                <div className="flex items-center gap-2 text-ink">
                  <FileSignature size={13} className="text-acid" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.22em]">
                    {release?.release_id || 'MOD-AUTH-2026-XXXX'}
                  </span>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-acid">
                  {locale === 'es' ? 'ACTIVO · FIRMADO' : 'ACTIVE · SIGNED'}
                </span>
              </div>

              <div className="grid grid-cols-2 divide-x divide-line">
                <div className="p-5 space-y-4">
                  <div>
                    <div className="text-eyebrow text-ink-muted mb-1">{locale === 'es' ? 'MODELO' : 'TALENT'}</div>
                    <div className="text-ink text-sm font-medium">{release?.talent?.name || 'Aurelia M.'}</div>
                    <div className="text-ink-dim text-xs">{release?.talent?.agency || 'Sight Management Studio'}</div>
                  </div>
                  <div>
                    <div className="text-eyebrow text-ink-muted mb-1">{locale === 'es' ? 'ALCANCE' : 'SCOPE'}</div>
                    <div className="text-ink text-sm capitalize">{release?.scope || 'commercial'}</div>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {(release?.permitted_uses || ['ficha de producto', 'publicidad', 'redes sociales']).map((u, i) => (
                        <span key={i} className="text-[9px] uppercase tracking-[0.18em] font-mono px-1.5 py-0.5 border border-line text-ink-muted">
                          {u.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-eyebrow text-ink-muted mb-1">{locale === 'es' ? 'TERRITORIO' : 'TERRITORY'}</div>
                    <div className="flex items-center gap-2 text-ink text-sm">
                      <Globe2 size={12} className="text-acid" />
                      {(release?.territory || ['UE', 'Reino Unido', 'EE.UU.']).join(' · ')}
                    </div>
                  </div>
                </div>

                <div className="p-5 space-y-4">
                  <div>
                    <div className="text-eyebrow text-ink-muted mb-1">{locale === 'es' ? 'CLÁUSULAS PARA IA' : 'AI CLAUSES'}</div>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2 text-ink">
                        <Check size={11} className="text-acid" /> {locale === 'es' ? 'Generación con IA permitida' : 'AI generation allowed'}
                      </div>
                      <div className="flex items-center gap-2 text-ink">
                        <X size={11} className="text-danger" /> {locale === 'es' ? 'Entrenamiento NO permitido' : 'Training NOT allowed'}
                      </div>
                      <div className="flex items-center gap-2 text-ink">
                        <Check size={11} className="text-acid" /> {locale === 'es' ? 'Cancelación a 30 días' : '30-day revocation'}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="text-eyebrow text-ink-muted mb-1">{locale === 'es' ? 'VIGENCIA' : 'VALIDITY'}</div>
                    <div className="text-ink text-sm">
                      {release?.valid_from || '2026-01-15'} → {release?.expires_at || '2027-01-14'}
                    </div>
                  </div>
                  <div>
                    <div className="text-eyebrow text-ink-muted mb-1">{locale === 'es' ? 'IMÁGENES VINCULADAS' : 'LINKED IMAGES'}</div>
                    <div className="font-display text-3xl text-acid leading-none">{release?.linked_assets_count ?? 47}</div>
                  </div>
                </div>
              </div>

              {/* Compliance checks */}
              <div className="border-t border-line-strong px-5 py-4 grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(release?.compliance_checks || {
                  alcance_coincide: true,
                  territorio_coincide: true,
                  permiso_activo: true,
                  clausula_anti_falsificacion: true,
                  sin_menores: true,
                }).slice(0, 6).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-1.5">
                    {v === true || (typeof v === 'string' && v) ? <Check size={11} className="text-acid shrink-0" /> : <X size={11} className="text-danger shrink-0" />}
                    <span className="text-[9px] uppercase tracking-[0.18em] font-mono text-ink-muted leading-tight">
                      {k.replace(/_/g, ' ')}
                    </span>
                  </div>
                ))}
              </div>

              <div className="border-t border-line-strong px-5 py-3 flex items-center justify-between bg-bg/40">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-dim">
                  {release?.footer?.issuer || (locale === 'es' ? 'MODARIA · Caja Fuerte de Permisos' : 'MODARIA · Vault of Permissions')}
                </span>
                <a
                  href={release?.footer?.verifiable_at || '#'}
                  className="font-mono text-[10px] uppercase tracking-[0.22em] text-acid flex items-center gap-1 hover:underline"
                  data-testid="likeness-verify-link"
                >
                  {locale === 'es' ? 'VERIFICAR' : 'VERIFY'} <ExternalLink size={10} />
                </a>
              </div>
            </motion.div>
            {release?.is_demo && (
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-ink-dim leading-snug" data-testid="likeness-demo-note">
                {locale === 'es'
                  ? 'AUTORIZACIÓN DE EJEMPLO · TU SISTEMA EN CASA ALMACENA Y FIRMA LAS REALES.'
                  : 'DEMO PERMISSION · YOUR IN-HOUSE SYSTEM STORES AND SIGNS THE REAL ONES.'}
              </p>
            )}
          </div>

          {/* Clause ticker side panel */}
          <div className="lg:col-span-3">
            <ClauseTicker locale={locale} />
            <div className="mt-3 text-eyebrow text-ink-dim leading-snug">
              {locale === 'es'
                ? 'EXTRACTO LITERAL DEL CONTRATO TIPO. EXPORTABLE EN PDF FIRMADO POR IMAGEN.'
                : 'LITERAL EXTRACT FROM THE TEMPLATE CONTRACT. EXPORTABLE AS SIGNED PDF PER IMAGE.'}
            </div>
          </div>
        </div>

        {/* CTA strip */}
        <div className="border-t border-line pt-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-7">
            <p className="text-ink text-base md:text-lg leading-relaxed">
              {locale === 'es'
                ? '¿Tienes un catálogo con caras reales y no sabes si están cubiertas? Hacemos una revisión gratuita en 48 horas.'
                : 'Got a catalogue with real faces and you\'re not sure they\'re covered? We run a free review in 48 hours.'}
            </p>
          </div>
          <div className="md:col-span-5 flex items-center gap-3 md:justify-end">
            <a
              href="#contact"
              className="btn-acid magnetic !text-[11px]"
              data-testid="likeness-cta-primary"
            >
              <Lock size={12} /> {locale === 'es' ? 'Cubre tus derechos' : 'Clear your rights'}
            </a>
            <a
              href="#compliance"
              className="btn-ghost magnetic !text-[11px]"
              data-testid="likeness-cta-secondary"
            >
              {locale === 'es' ? 'Ver Informe de Cumplimiento' : 'See Compliance Report'}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
